/**
 * Conversation Data Model
 *
 * Represents open conversations for coordination and discussion
 * Used for Part 2: Open Conversations (Minimal MVP)
 *
 * @module models/Conversation
 */

import { serverTimestamp } from 'firebase/firestore';

// ============================================================================
// CONVERSATION TYPES
// ============================================================================

export const CONVERSATION_TYPES = {
  DIRECT: 'direct',                   // 1:1 conversation between two users
  STUDENT_CENTRIC: 'student-centric', // Discussion about a specific student
  GENERAL: 'general',                 // Staff-only general topics
  GROUP: 'group',                     // Group chats
  SMART_MESSAGES: 'smart_messages',   // Dedicated Smart Messages thread
  BROADCAST: 'broadcast',             // School / grade announcement channels
};

// School-managed conversations are tagged with a sourceType so triggers and
// the reconciler can find them. See functions/services/schoolGroups/config.js
// for the canonical list.
//   staff   — staff-only group (sourceId = schoolId)
//   school  — school-wide broadcast (sourceId = schoolId)
//   parents — all parents (sourceId = schoolId)
//   grade   — per-grade broadcast (sourceId = gradeId)
export const SOURCE_TYPES = {
  STAFF: 'staff',
  SCHOOL: 'school',
  PARENTS: 'parents',
  GRADE: 'grade',
};

export const CONVERSATION_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived'
};

export const PARTICIPANT_ROLES = {
  TEACHER: 'teacher',
  ADMIN: 'admin',
  PARENT: 'parent'
};

// ============================================================================
// MESSAGE TYPES
// ============================================================================

export const MESSAGE_TYPES = {
  TEXT: 'text',             // Regular text message
  IMAGE: 'image',           // Image message (Phase 3)
  FILE: 'file',             // File attachment (Phase 3)
  VOICE: 'voice',           // Voice note (Phase 3)
  SYSTEM: 'system',         // System notification (user joined, etc.)
  // Thread A stubs — renderers land with their features (see messageRenderers.js)
  IMAGE_ALBUM: 'image_album', // Multiple images collated into one bubble
  VIDEO: 'video',             // Video message
  LOCATION: 'location',       // Shared location / place
  EVENT: 'event'              // Calendar event card
};

export const MESSAGE_STATUS = {
  SENDING: 'sending',
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read'
};

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

/**
 * Create participant object
 */
export function createParticipant(userId, name, role) {
  return {
    userId,
    name,
    role,
    joinedAt: new Date(),
    active: true
  };
}

/**
 * Create conversation document
 * Returns object ready for Firestore (without ID)
 */
export function createConversationDocument({
  schoolId,
  type = CONVERSATION_TYPES.STUDENT_CENTRIC,
  subject,
  studentId = null,
  studentName = null,
  participants = [],
  createdBy,
  relatedMentionId = null,
  relatedConsequenceId = null
}) {
  // Validation
  if (!schoolId) throw new Error('schoolId is required');
  if (!subject) throw new Error('subject is required');
  if (!createdBy) throw new Error('createdBy is required');
  if (!participants || participants.length === 0) {
    throw new Error('At least one participant is required');
  }

  // Initialize unread counts only — lastReadTimestamps left empty intentionally
  // so single ✓ (unread) state works correctly before anyone opens the conversation
  const unreadCounts = {};
  const lastReadTimestamps = {};
  participants.forEach(p => {
    unreadCounts[p.userId] = 0;
  });

  // Flat array of participant IDs for efficient Firestore array-contains queries
  const participantIds = participants.map(p => p.userId);

  return {
    // Identity
    schoolId,
    type,
    subject,

    // Context
    studentId,
    studentName,
    relatedMentionId,
    relatedConsequenceId,

    // Participants
    participants,
    participantIds,

    // Group metadata
    groupName: null,
    groupAvatar: null,
    groupDescription: null,
    admins: [],

    // Pinning
    pinnedBy: [],
    pinnedMessages: [],

    // State
    createdAt: serverTimestamp(),
    createdBy,
    lastMessageAt: serverTimestamp(),
    lastMessage: null,
    status: CONVERSATION_STATUS.ACTIVE,

    // Per-user metadata
    unreadCounts,
    lastReadTimestamps,
    archivedBy: [],
    mutedBy: [],

    // Typing indicator map: { [userId]: Timestamp }
    typing: {},

    // Engagement
    messageCount: 0
  };
}

/**
 * Create message document for conversation
 * Returns object ready for Firestore (without ID)
 */
export function createMessageDocument({
  senderId,
  senderName,
  senderRole,
  text,
  type = MESSAGE_TYPES.TEXT,
  replyTo = null,
  media = null,
  forwardedFrom = null
}) {
  // Validation
  if (!senderId) throw new Error('senderId is required');
  if (!senderName) throw new Error('senderName is required');
  if (!senderRole) throw new Error('senderRole is required');
  if (!text && type === MESSAGE_TYPES.TEXT) {
    throw new Error('text is required for text messages');
  }

  return {
    // Sender
    senderId,
    senderName,
    senderRole,

    // Content
    type,
    text: text || '',

    // Media (Phase 3)
    media,

    // Reply context (denormalized for zero extra reads)
    replyTo,

    // Forwarding (Phase 7)
    forwardedFrom,

    // Status
    status: MESSAGE_STATUS.SENDING,

    // Reactions: { [emoji]: string[] }
    reactions: {},

    // Deletion
    deletedFor: [],
    deletedForEveryone: false,

    // Timestamps
    sentAt: serverTimestamp(),
    editedAt: null,

    // Read receipts
    readBy: []
  };
}

/**
 * Create system message (user joined, etc.)
 */
export function createSystemMessage(text) {
  return {
    senderId: 'system',
    senderName: 'System',
    senderRole: 'system',
    type: MESSAGE_TYPES.SYSTEM,
    text,
    sentAt: serverTimestamp(),
    editedAt: null,
    readBy: []
  };
}

/**
 * Create last message summary for conversation document
 */
export function createLastMessageSummary(senderId, senderName, text) {
  return {
    senderId,
    senderName,
    text: text.length > 100 ? text.substring(0, 100) + '...' : text,
    timestamp: serverTimestamp()
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Coerce a conversation's `participants` field to an array.
 *
 * Almost all docs store `participants` as an array, but a few legacy/demo docs
 * stored it as a map keyed by userId (e.g. `{ "<uid>": { ... } }`). Optional
 * chaining (`participants?.filter`) does NOT guard those — the value is truthy,
 * so `.filter`/`.find` throws "filter is not a function". Normalize on read so
 * every consumer always receives a clean array.
 *
 * @param {Object} data - Raw conversation data from Firestore
 * @returns {Object} Same data with `participants` guaranteed to be an array
 */
export function normalizeConversation(data) {
  if (!data) return data;
  const p = data.participants;
  if (Array.isArray(p)) return data;
  if (p && typeof p === 'object') {
    // Map keyed by userId → array of participant objects, backfilling userId.
    return {
      ...data,
      participants: Object.entries(p).map(([userId, value]) =>
        value && typeof value === 'object' ? { userId, ...value } : { userId }
      )
    };
  }
  return { ...data, participants: [] };
}

/**
 * Check if user is participant in conversation
 */
export function isParticipant(conversation, userId) {
  return conversation.participants.some(p => p.userId === userId && p.active);
}

/**
 * Get unread count for user
 */
export function getUnreadCount(conversation, userId) {
  return conversation.unreadCounts?.[userId] || 0;
}

/**
 * Check if conversation is archived for user
 */
export function isArchivedForUser(conversation, userId) {
  return conversation.archivedBy?.includes(userId) || false;
}

/**
 * Check if conversation is muted for user
 */
export function isMutedForUser(conversation, userId) {
  return conversation.mutedBy?.includes(userId) || false;
}

/**
 * Get conversation display name
 * For student-centric: "Re: [Student Name]"
 * For general: subject
 */
export function getConversationDisplayName(conversation) {
  if (conversation.type === CONVERSATION_TYPES.STUDENT_CENTRIC && conversation.studentName) {
    return `Re: ${conversation.studentName}`;
  }
  return conversation.subject;
}

/**
 * Get participant names (excluding current user)
 */
export function getOtherParticipantNames(conversation, currentUserId) {
  return conversation.participants
    .filter(p => p.userId !== currentUserId && p.active)
    .map(p => p.name)
    .join(', ');
}

/**
 * Add read receipt to message
 */
export function addReadReceipt(message, userId) {
  // Check if user already read this message
  if (message.readBy?.some(r => r.userId === userId)) {
    return message.readBy;
  }

  return [
    ...(message.readBy || []),
    {
      userId,
      readAt: new Date()
    }
  ];
}

/**
 * Check if message has been read by user
 */
export function hasUserReadMessage(message, userId) {
  return message.readBy?.some(r => r.userId === userId) || false;
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validate conversation type
 */
export function isValidConversationType(type) {
  return Object.values(CONVERSATION_TYPES).includes(type);
}

/**
 * Validate participant role
 */
export function isValidParticipantRole(role) {
  return Object.values(PARTICIPANT_ROLES).includes(role);
}

/**
 * Validate message type
 */
export function isValidMessageType(type) {
  return Object.values(MESSAGE_TYPES).includes(type);
}

export default {
  CONVERSATION_TYPES,
  SOURCE_TYPES,
  CONVERSATION_STATUS,
  PARTICIPANT_ROLES,
  MESSAGE_TYPES,
  MESSAGE_STATUS,
  createParticipant,
  createConversationDocument,
  createMessageDocument,
  createSystemMessage,
  createLastMessageSummary,
  isParticipant,
  getUnreadCount,
  isArchivedForUser,
  isMutedForUser,
  getConversationDisplayName,
  getOtherParticipantNames,
  addReadReceipt,
  hasUserReadMessage,
  isValidConversationType,
  isValidParticipantRole,
  isValidMessageType
};
